/**
 * SupportSessionBanner. Shown for as long as a TutorLoop support session is
 * signed in (an access token with a `support_by` claim). Says who is here,
 * how long is left, and offers "End session", which signs out. When the time
 * runs out the session is ended for them.
 */

import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { LifeBuoy } from "lucide-react";
import { useEffect, useState } from "react";

import { useAuth } from "@/hooks/useAuth";
import { readSupportSession } from "@/lib/api/supportSession";

const TICK_MS = 15_000;

function timeLeft(ms: number): string {
  if (ms <= 0) return "Session ending";
  const minutes = Math.ceil(ms / 60_000);
  if (minutes <= 1) return "Less than a minute left";
  return `${minutes} minutes left`;
}

export function SupportSessionBanner() {
  const { user, logout } = useAuth();
  const reduce = useReducedMotion();
  const [now, setNow] = useState(() => Date.now());

  // Re-read the token whenever the signed-in user changes.
  const session = user ? readSupportSession() : null;
  const expiresAt = session?.expiresAt ?? null;

  useEffect(() => {
    if (expiresAt === null) return;
    const id = window.setInterval(() => setNow(Date.now()), TICK_MS);
    setNow(Date.now());
    return () => window.clearInterval(id);
  }, [expiresAt]);

  useEffect(() => {
    if (expiresAt === null) return;
    const wait = Math.max(0, expiresAt - Date.now());
    const id = window.setTimeout(() => void logout(), wait);
    return () => window.clearTimeout(id);
  }, [expiresAt, logout]);

  return (
    <AnimatePresence>
      {session ? (
        <motion.div
          role="status"
          aria-live="polite"
          initial={{ opacity: 0, y: reduce ? 0 : 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: reduce ? 0 : 12 }}
          transition={{ duration: reduce ? 0 : 0.28, ease: [0.22, 1, 0.36, 1] }}
          style={{
            position: "fixed",
            zIndex: "var(--z-toast)" as unknown as number,
            left: "50%",
            translate: "-50% 0",
            bottom: "calc(var(--tabbar-h, 0px) + max(env(safe-area-inset-bottom), 16px))",
            width: "min(560px, calc(100vw - 32px))",
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: "var(--space-3)",
            padding: "var(--space-3) var(--space-4)",
            borderRadius: "var(--radius-lg)",
            background: "var(--color-command-bar-bg)",
            color: "var(--color-command-bar-text)",
            boxShadow: "var(--shadow-modal)",
          }}
        >
          <LifeBuoy size={18} strokeWidth={1.5} aria-hidden style={{ flex: "none", color: "var(--color-command-bar-icon-hover)" }} />
          <div style={{ flex: "1 1 220px", minWidth: 0 }}>
            <div style={{ font: "600 14px/1.3 var(--font-sans)" }}>TutorLoop support is signed in</div>
            <div style={{ font: "400 13px/1.4 var(--font-sans)", color: "var(--color-command-bar-muted)" }}>
              {session.by} · {timeLeft(session.expiresAt - now)}
            </div>
          </div>
          <button
            type="button"
            onClick={() => void logout()}
            style={{
              minHeight: 44,
              padding: "0 var(--space-5)",
              borderRadius: "var(--radius-pill)",
              border: "1px solid var(--color-command-bar-divider)",
              background: "var(--color-command-bar-hover-surface)",
              color: "var(--color-command-bar-text)",
              font: "600 13px/1 var(--font-sans)",
              cursor: "pointer",
            }}
          >
            End session
          </button>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
